import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {defer, EMPTY, merge, Observable, of, Subject} from 'rxjs';
import {debounceTime, repeat, share, switchMapTo, takeUntil, tap} from 'rxjs/operators';
import {bindWith} from '../../../common/rxjs/bind-with';
import {AccountService} from '../../../components/accounts';
import {isSmartSecurity} from '../../../components/smart-security';
import {OrderStatus} from '../../../enums/order-status';
import {ExchangeAccount} from '../../../models/exchange-account';
import {Order} from '../../../models/order';
import {
    UserDataStreamingService,
    UserDataWebSocketMessage,
    UserDataWebSocketMessageType
} from '../../../services/user-data-streaming';

export interface OrdersSnapshotWsMessage extends UserDataWebSocketMessage {
    messageType: UserDataWebSocketMessageType.ORDER;
    data: Order[];
}

export interface OrderUpdateWsMessage extends UserDataWebSocketMessage {
    messageType: UserDataWebSocketMessageType.ORDER_SNAPSHOT;
    data: Order;
}

interface OrderRef {
    order: Order;
    index: number;
}

@Injectable()
export class OrderStreamingService {

    private static isOrdersSnapshotMessage(message: UserDataWebSocketMessage): message is OrdersSnapshotWsMessage {
        return message.messageType === UserDataWebSocketMessageType.ORDER_SNAPSHOT;
    }

    private static isOrderUpdateMessage(message: UserDataWebSocketMessage): message is OrderUpdateWsMessage {
        return message.messageType === UserDataWebSocketMessageType.ORDER;
    }

    private static isSmartRelatedNormalOrder(order: Order): boolean {
        return !!order.smartOrigId;
    }

    private static isOrderReplaced(order: Order): boolean {
        const {state} = order;

        return !!state && state.orderStatus === OrderStatus.REPLACED;
    }

    public readonly order$: Observable<Order>;
    public readonly deprecateOrder$: Observable<Order>;
    public readonly orderUpdate$: Observable<Order>;

    private orderRefMapByClOrdId: {[clOrdId: string]: OrderRef} = {};

    private orders: Order[] = [];

    private readonly deprecateOrderSubject = new Subject<Order>();

    private readonly orderSubject = new Subject<Order>();

    private readonly orderUpdateSubject = new Subject<Order>();

    private readonly followUpOrder$: Observable<Order>;

    constructor(
        private userDataStreamingService: UserDataStreamingService,
        private accountService: AccountService
    ) {
        this.followUpOrder$ = this.orderSubject.pipe(
            bindWith(this.listenToAccounts()),
            bindWith(this.userDataStreamingService.messages$)
        );

        this.order$ = this.createOrderStream();
        this.deprecateOrder$ = this.deprecateOrderSubject.asObservable();
        this.orderUpdate$ = this.createOrderUpdatesStream();

        this.listenToUserData();
    }

    public readOrdersSnapshotMessage(ordersSnapshot: Order[]): void {
        ordersSnapshot.forEach(orderData => this.readOrderUpdateMessage(orderData));
    }

    public readOrderUpdateMessage(orderData: Order): void {
        if (!OrderStreamingService.isSmartRelatedNormalOrder(orderData)) {
            const orderRef: OrderRef = this.orderRefMapByClOrdId[orderData.clOrdId];

            const order = orderRef
                ? this.readOrderUpdate(orderRef, orderData)
                : this.readOrderCreate(orderData);

            if (order) {
                this.orderSubject.next(order);
            }
        }
    }

    private readOrderUpdate(orderRef: OrderRef, orderData: Order): Order {
        const order: Order = orderRef.order;

        order.state = {
            ...orderRef.order.state,
            ...orderData.state
        };

        const needDeprecate: boolean = OrderStreamingService.isOrderReplaced(order);

        if (needDeprecate) {
            this.deprecateOrder(order.clOrdId);
        } else {
            this.orderUpdateSubject.next(orderRef.order);
        }

        return needDeprecate ? null : order;
    }

    private readOrderCreate(orderData: Order): Order {
        const order = orderData as Order;
        const isOrderReplaced = OrderStreamingService.isOrderReplaced(order);

        if (!isOrderReplaced) {
            this.orderRefMapByClOrdId[order.clOrdId] = {
                order,
                index: this.orders.push(order) - 1
            };

            const needDeprecate: boolean = !!order.origClOrdId
                && order.state.orderStatus !== OrderStatus.REJECTED;

            if (needDeprecate) {
                this.deprecateOrder(order.origClOrdId);
            }

            this.orderUpdateSubject.next(order);
        }

        return isOrderReplaced ? null : order;
    }

    private deprecateOrder(clOrdId: string): void {
        const orderRef: OrderRef = this.orderRefMapByClOrdId[clOrdId];

        if (orderRef) {
            delete this.orderRefMapByClOrdId[clOrdId];
            this.orders.splice(orderRef.index, 1);

            // Shift down upper indexes.
            this.orders.slice(orderRef.index)
                .forEach(order => this.orderRefMapByClOrdId[order.clOrdId].index--);

            this.deprecateOrderSubject.next(orderRef.order);
        }
    }

    private createOrderStream(): Observable<Order> {
        const initialOrders$ = defer((): Observable<Order> =>
            this.orders
                ? of(...this.orders)
                : EMPTY
        );

        return merge(
            initialOrders$,
            this.followUpOrder$
        );
    }

    private createOrderUpdatesStream(): Observable<Order> {
        const SNAPSHOT_TIME_LIMIT = 5000;

        return this.userDataStreamingService.subscriptionSent$.pipe(
            debounceTime(SNAPSHOT_TIME_LIMIT), // Wait some time to allow snapshot pass.
            switchMapTo(this.followUpOrder$),
            takeUntil(this.userDataStreamingService.disconnected$),
            repeat(),
            bindWith(this.userDataStreamingService.messages$),
            share()
        );
    }

    private listenToUserData(): void {
        this.userDataStreamingService.registerCoworker({
            handleMessage: message => {
                if (OrderStreamingService.isOrdersSnapshotMessage(message)) {
                    this.readOrdersSnapshotMessage(message.data);
                } else if (OrderStreamingService.isOrderUpdateMessage(message)) {
                    this.readOrderUpdateMessage(message.data);
                }
            },

            cleanUp: () => {
                this.orders.length = 0;
                this.orderRefMapByClOrdId = {};
            }
        });
    }

    private listenToAccounts(): Observable<any> {
        return this.accountService.accounts$.pipe(
            tap(accounts => this.deprecateOrdersOfOtherAccounts(accounts)),
            share()
        );
    }

    private deprecateOrdersOfOtherAccounts(accounts: ExchangeAccount[]): void {
        if (this.orders.length) {
            const accountByIdDict = _.keyBy<ExchangeAccount>(accounts, account => account.id);
            const ordersToDeprecate = this.orders.filter(order =>

                // TODO: [HED-418] Review
                !isSmartSecurity(order.security) && !accountByIdDict[order.accountId]
            );

            ordersToDeprecate.forEach(order => this.deprecateOrder(order.clOrdId));
        }
    }

}
